import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Collapse, Typography } from '@mui/material';

import CopyButton from 'main/components/buttons/CopyButton';
import DownloadButton from 'main/components/buttons/DownloadButton';
import Hljs from 'main/components/Hljs';
import ProtectedData from 'pages/item/v2/components/ProtectedData';
import isEmpty from 'utils/is-empty';

const useStyles = makeStyles<void, 'actionButtons'>({ name: 'Code' })((theme, _, classes) => ({
    sourceData: {
        background: '#38383A',
        position: 'relative',
        padding: 20,
        borderRadius: 18,
        marginTop: 52,
        '& pre': {
            margin: '0'
        },
        [`&:hover .${classes.actionButtons}`]: {
            display: 'flex'
        }
    },
    actionButtons: {
        background: '#58585a',
        padding: 8,
        borderRadius: 8,
        position: 'absolute',
        right: 20,
        display: 'none'
    },
    actionButtonsSeparator: {
        borderRight: '1px solid #38383A',
        marginRight: 8,
        paddingLeft: 8
    },
    more: {
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        '&>p, >svg': {
            fontWeight: 600,
            color: theme.palette.primary.main
        }
    },
    icon: {
        transition: 'all 0.3s ease-out'
    },
    mask: {
        maskImage: 'linear-gradient(to top, transparent 0%, black 18%)'
    }
}));

interface Props {
    sourceData: string;
}

const Code: React.FC<Props> = ({ sourceData }) => {
    const { classes, cx } = useStyles();
    const [isOpen, toggleOpen] = useReducer((state) => !state, false);
    const [collapseSize, setCollapseSize] = useState(0);

    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Till the end of current view
        // ref.current.clientHeight > (window.innerHeight - ref.current.offsetTop) && setCollapseSize(window.innerHeight - ref.current.offsetTop - 80)
        // Till the end of page size
        if (ref.current && ref.current.clientHeight > window.innerHeight) {
            setCollapseSize(window.innerHeight);
        }
    }, []);

    const code = useMemo(() => {
        switch (true) {
            case isEmpty(sourceData):
                return '';
            case sourceData.endsWith('='):
                try {
                    return atob(sourceData);
                } catch (error) {
                    console.warn("Can't extract Base64 source code", error);
                    return sourceData;
                }
            default:
                return sourceData;
        }
    }, [sourceData]);

    const content = useMemo(
        () => (
            <ProtectedData data={code}>
                <div className={classes.actionButtons}>
                    <CopyButton iconButton value={code} />
                    <div className={classes.actionButtonsSeparator} />
                    <DownloadButton iconButton value={code} />
                </div>
                <Hljs language="bash">{code}</Hljs>
            </ProtectedData>
        ),
        [classes.actionButtons, classes.actionButtonsSeparator, code]
    );

    if (!code) {
        return null;
    }

    return (
        <div className={classes.sourceData} ref={ref}>
            {collapseSize ? (
                <>
                    <Collapse
                        in={isOpen}
                        collapsedSize={collapseSize}
                        classes={{ root: cx({ [classes.mask]: isOpen === false }) }}
                    >
                        {content}
                    </Collapse>
                    <div className={classes.more} onClick={toggleOpen}>
                        <Typography variant="body2">{isOpen ? 'Less' : 'More'}</Typography>
                        <KeyboardArrowDown
                            className={classes.icon}
                            style={{ transform: isOpen ? 'rotate(-180deg)' : 'initial' }}
                        />
                    </div>
                </>
            ) : (
                content
            )}
        </div>
    );
};

export default Code;
